import React, { useState, useMemo, useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  InputBase,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  makeStyles,
  Theme,
  Popover,
  Chip,
  OutlinedInput,
  CircularProgress,
  TextField,
} from "@material-ui/core";
import {
  CloudQueue,
  Add,
  Close,
  Search,
  FilterList,
  Sort,
  GetApp,
  DeleteOutline,
  Description,
  Image as ImageIcon,
  ViewModule, // <-- ADD THIS
  ViewList, // <-- ADD THIS
  Edit,
  Replay,
  FileCopyOutlined,
  InfoOutlined,
} from "@material-ui/icons";
import { fetchIntakeImagesUncategorized } from "../../common/graphqlQueries";
import { useFetchContext } from "../../common/FetchContext";
import { formatDate, formatDateDate } from "../../common/formatDate";
import JSZip from "jszip";
import { saveAs } from "file-saver";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { useCloudImageUpload } from "../../dataEgress/dataEgressMutations";

// Update Interface
export interface FileData {
  id: string;
  name: string;
  caption: string;
  type: "photo" | "pdf" | "doc" | "zip" | "xls" | "csv";
  typeLabel: string;
  uploadedBy: string;
  date: string;
  rawDate: string | number; // <-- ADD THIS (so we can compare dates accurately)
  size: string;
  imageUrl?: string;
}

// --- Styles ---
const useStyles = makeStyles((theme: Theme) => ({
  dialogPaper: {
    minWidth: "90vw",
    minHeight: "85vh",
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
    borderRadius: 8,
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderBottom: "1px solid #e0e0e0",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(1, 3),
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#f9fafb",
    gap: theme.spacing(2),
  },
  chipRow: {
    display: "flex",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderBottom: "1px solid #e0e0e0",
    backgroundColor: "#ffffff",
    gap: theme.spacing(1),
    overflowX: "auto",
  },
  searchBox: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#fff",
    border: "1px solid #e0e0e0",
    borderRadius: 4,
    padding: theme.spacing(0.5, 1.5),
    flexGrow: 1,
    maxWidth: 400,
  },
  mainContent: {
    display: "flex",
    flexGrow: 1,
    overflow: "hidden",
  },
  leftPane: {
    flex: 2,
    overflowY: "auto",
    borderRight: "1px solid #e0e0e0",
    transition: "flex 0.3s ease",
  },
  rightPane: {
    flex: 1,
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    backgroundColor: "#ffffff",
    borderLeft: "1px solid #e0e0e0",
    overflowY: "auto", // Added to ensure it can scroll if needed
  },
  footer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: theme.spacing(2, 3),
    borderTop: "1px solid #e0e0e0",
    backgroundColor: "#f9fafb",
  },
  previewBox: {
    backgroundColor: "#2c2c2c",
    flex: 1, // Expands to fill available space
    minHeight: 250,
    borderRadius: 8,
    marginBottom: theme.spacing(2),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    overflow: "hidden",
  },
  actionButton: {
    textTransform: "none",
    fontWeight: 400,
  },
  iconBox: {
    padding: theme.spacing(1),
    borderRadius: 4,
    display: "flex",
    color: "#fff",
  },
  popoverPaper: {
    width: 480,
    padding: theme.spacing(3),
    borderRadius: 8,
    boxShadow: "0px 4px 20px rgba(0, 0, 0, 0.15)",
  },
  filterSectionLabel: {
    fontSize: 11,
    fontWeight: 600,
    color: "#888",
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
  },
  filterBtn: {
    textTransform: "none",
    borderRadius: 20,
    padding: "4px 16px",
    marginRight: 8,
    marginBottom: 8,
    border: "1px solid #e0e0e0",
    color: "#555",
  },
  filterBtnActive: {
    borderColor: "#1a73e8",
    color: "#1a73e8",
    backgroundColor: "#f8faff",
  },
  chipActive: {
    backgroundColor: "#e8f0fe",
    color: "#1a73e8",
    fontWeight: 500,
    border: "1px solid #cce0ff",
  },
  gridContainer: {
    display: "flex",
    flexWrap: "wrap",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
  },
  gridCard: {
    width: 180,
    border: "1px solid #e0e0e0",
    borderRadius: 8,
    padding: theme.spacing(1),
    cursor: "pointer",
    position: "relative",
    display: "flex",
    flexDirection: "column",
    "&:hover": {
      backgroundColor: "#f8faff",
    },
  },
  gridCardSelected: {
    backgroundColor: "#f8faff",
    borderColor: "#1a73e8",
  },
  gridCardPreview: {
    height: 120,
    backgroundColor: "#ffffff",
    borderRadius: 4,
    marginBottom: theme.spacing(1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#999",
    overflow: "hidden",
  },
  loadingContainer: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    height: "100%",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
}));

const getFileTypeAndLabel = (
  img: any,
): { type: FileData["type"]; typeLabel: string } => {
  const url = img.imageUrl || img.imagePath || "";
  const name = img.fileName || img.name || img.caption || "";
  const rawType = (img.fileType || img.type || "").toLowerCase();

  // 1. Check explicit type/fileType from API if present
  if (rawType.includes("pdf") || rawType === "pdf")
    return { type: "pdf", typeLabel: "PDF" };
  if (rawType.includes("doc") || rawType === "docx" || rawType === "word")
    return { type: "doc", typeLabel: "DOC" };
  if (rawType.includes("xls") || rawType === "xlsx" || rawType === "excel")
    return { type: "xls", typeLabel: "XLS" };
  if (rawType.includes("zip") || rawType === "archive")
    return { type: "zip", typeLabel: "ZIP" };
  if (rawType.includes("csv")) return { type: "csv", typeLabel: "CSV" };
  if (
    rawType.includes("photo") ||
    rawType.includes("image") ||
    rawType === "jpg" ||
    rawType === "png"
  )
    return { type: "photo", typeLabel: "Photo" };

  // 2. Check extension from filename or URL
  const targetStr = (name + " " + url).toLowerCase();
  if (targetStr.endsWith(".pdf") || targetStr.includes(".pdf?"))
    return { type: "pdf", typeLabel: "PDF" };
  if (
    targetStr.endsWith(".doc") ||
    targetStr.endsWith(".docx") ||
    targetStr.includes(".doc?") ||
    targetStr.includes(".docx?")
  )
    return { type: "doc", typeLabel: "DOC" };
  if (
    targetStr.endsWith(".xls") ||
    targetStr.endsWith(".xlsx") ||
    targetStr.includes(".xls?") ||
    targetStr.includes(".xlsx?")
  )
    return { type: "xls", typeLabel: "XLS" };
  if (targetStr.endsWith(".zip") || targetStr.includes(".zip?"))
    return { type: "zip", typeLabel: "ZIP" };
  if (targetStr.endsWith(".csv") || targetStr.includes(".csv?"))
    return { type: "csv", typeLabel: "CSV" };
  if (
    targetStr.endsWith(".png") ||
    targetStr.endsWith(".jpg") ||
    targetStr.endsWith(".jpeg") ||
    targetStr.endsWith(".webp") ||
    targetStr.endsWith(".gif")
  )
    return { type: "photo", typeLabel: "Photo" };

  // 3. Fallback check based on URL structure
  if (
    url &&
    (url.match(/\.(jpeg|jpg|png|gif|webp)/i) || !url.match(/\.\w{3,4}(\?|$)/))
  ) {
    return { type: "photo", typeLabel: "Photo" };
  }

  return { type: "doc", typeLabel: "DOC" };
};

const getFileIcon = (type: string, classes: Record<string, string>) => {
  const labelMap: Record<string, { bg: string; text: string }> = {
    photo: { bg: "#2c3e50", text: "IMG" },
    pdf: { bg: "#e53935", text: "PDF" },
    doc: { bg: "#1e88e5", text: "DOC" },
    xls: { bg: "#43a047", text: "XLS" },
    csv: { bg: "#00acc1", text: "CSV" },
    zip: { bg: "#fb8c00", text: "ZIP" },
  };

  const config = labelMap[type] || { bg: "#757575", text: type.toUpperCase() };

  return (
    <Box
      bgcolor={config.bg}
      style={{
        width: 40,
        height: 40,
        flexShrink: 0,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontWeight: 700,
        fontSize: 11,
        color: "#fff",
        borderRadius: 4,
      }}
    >
      {config.text}
    </Box>
  );
};

// --- HELPER: EXTRACT & CLEAN FILENAME FROM AZURE URL ---
const getCleanFileName = (imageUrl: string, caption?: string): string => {
  if (!imageUrl)
    return caption && caption !== "No caption" ? caption : "Untitled File";

  try {
    // 1. Get the last segment of the URL and strip query parameters
    const rawName = decodeURIComponent(
      imageUrl.split("/").pop()?.split("?")[0] || "",
    );

    // 2. Strip the leading Azure storage 36-character UUID/GUID prefix if present
    const cleanedName = rawName.replace(
      /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}_/i,
      "",
    );

    return (
      cleanedName ||
      (caption && caption !== "No caption" ? caption : "Untitled File")
    );
  } catch (e) {
    return caption && caption !== "No caption" ? caption : "Untitled File";
  }
};

const getDateCategoryLabel = (rawDate: string | number): string => {
  if (!rawDate) return "Unknown Date";
  const fileDate = new Date(rawDate);
  const today = new Date();

  const isToday = fileDate.toDateString() === today.toDateString();

  const yesterday = new Date();
  yesterday.setDate(today.getDate() - 1);
  const isYesterday = fileDate.toDateString() === yesterday.toDateString();

  if (isToday) return "Today";
  if (isYesterday) return "Yesterday";

  const year = fileDate.getFullYear();
  const month = String(fileDate.getMonth() + 1).padStart(2, "0");
  const day = String(fileDate.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

interface CloudDialogProps {
  open: boolean;
  onClose: () => void;
}

export const CloudFileManagerDialog: React.FC<CloudDialogProps> = ({
  open,
  onClose,
}) => {
  const classes = useStyles();
  const { authAxios } = useFetchContext();
  const history = useHistory();
  const { mutateAsync: CloudFileUpload } = useCloudImageUpload();

  // --- STATE ---
  const [apiFiles, setApiFiles] = useState<FileData[]>([]);
  const [loading, setLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);

  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [activePreview, setActivePreview] = useState<FileData | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [displayLimit, setDisplayLimit] = useState(40);
  const [viewMode, setViewMode] = useState<"list" | "grid">("list");
  const [filterAnchorEl, setFilterAnchorEl] =
    useState<HTMLButtonElement | null>(null);
  const [sortOrder, setSortOrder] = useState<"newest" | "oldest">("newest");
  const [editingSingle, setEditingSingle] = useState(false);
  const [singleDraft, setSingleDraft] = useState("");
  const [bulkDrafts, setBulkDrafts] = useState<Record<string, string>>({});
  const [sameCaption, setSameCaption] = useState("");
  const [savingCaptions, setSavingCaptions] = useState(false);

  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const [filters, setFilters] = useState({
    tab: "all" as "all" | "photos" | "files",
    fileTypes: [] as string[],
    date: "",
    uploadedBy: [] as string[],
  });

  // --- RESET HANDLER ---
  const handleClose = () => {
    setSelectedIds([]);
    setActivePreview(null);
    setSearchQuery("");
    setFilters({ tab: "all", fileTypes: [], date: "", uploadedBy: [] });
    setCustomStartDate("");
    setCustomEndDate("");
    setFilterAnchorEl(null);
    setEditingSingle(false);
    setBulkDrafts({});
    setSameCaption("");
    onClose();
  };

  // --- HELPER: GET PROPER FILENAME & EXTENSION ---
  const getProperFileName = (file: FileData, index: number = 0) => {
    let extension = "bin";
    let baseName =
      file.name && file.name !== "Untitled Image" ? file.name : `download`;

    // Strip existing extension from the baseName if it exists
    if (baseName.includes(".")) {
      const parts = baseName.split(".");
      extension = parts.pop() || "bin";
      baseName = parts.join(".");
    }
    // Try to get extension from URL
    else if (file.imageUrl) {
      try {
        const urlWithoutQuery = file.imageUrl.split("?")[0];
        const parts = urlWithoutQuery.split(".");
        if (parts.length > 1) {
          extension = parts.pop() as string;
        }
      } catch (e) {}
    }

    // Fallback to type mapping
    if (extension === "bin") {
      const typeMap: Record<string, string> = {
        photo: "jpg",
        pdf: "pdf",
        doc: "docx",
        xls: "xlsx",
        csv: "csv",
        zip: "zip",
      };
      extension = typeMap[file.type] || "bin";
    }

    // Append the index to guarantee the ZIP doesn't overwrite files with the same name!
    return `${baseName}_${index + 1}.${extension}`;
  };

  // --- BATCH DOWNLOAD HANDLER (MULTIPLE FILES = ZIP) ---
  const handleBatchDownload = async () => {
    if (selectedIds.length === 0) return;

    const filesToDownload = apiFiles.filter((file) =>
      selectedIds.includes(file.id),
    );

    // 1. If only ONE file is selected, download it normally (no zip)
    if (filesToDownload.length === 1) {
      return handleDownload(filesToDownload[0]);
    }

    // 2. If MULTIPLE files are selected, bundle them into a ZIP
    try {
      const zip = new JSZip();
      let hasFiles = false;

      // Optional: You can set a loading state here if you have one,
      // e.g., setDownloading(true)

      for (let i = 0; i < filesToDownload.length; i++) {
        const file = filesToDownload[i];
        if (!file.imageUrl) continue;

        try {
          // Fetch the file as a binary blob
          const response = await authAxios.get(file.imageUrl, {
            responseType: "blob",
          });

          // Generate a safe name with extension using your helper
          const fileName = getProperFileName(file, i);

          // Add the file to our zip folder
          zip.file(fileName, response.data);
          hasFiles = true;
        } catch (error) {
          console.error(`Error fetching file ${file.name} for zip:`, error);
        }
      }

      // 3. Generate the ZIP and trigger a single download
      if (hasFiles) {
        const zipBlob = await zip.generateAsync({ type: "blob" });
        saveAs(zipBlob, "LSDB_Cloud_Files.zip");
      }
    } catch (error) {
      console.error("Error creating zip file:", error);
    } finally {
      // Optional: clear loading state here
      // setDownloading(false)
    }
  };

  // --- SINGLE DOWNLOAD HANDLER ---
  const handleDownload = async (file: FileData) => {
    if (!file.imageUrl) return;

    try {
      const response = await authAxios.get(file.imageUrl, {
        responseType: "blob",
      });

      const blob = new Blob([response.data]);
      const blobUrl = window.URL.createObjectURL(blob);

      const link = document.createElement("a");
      link.href = blobUrl;

      // Use the helper to force the correct extension!
      link.download = getProperFileName(file);

      document.body.appendChild(link);
      link.click();

      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error(
        "Error downloading file, falling back to direct link:",
        error,
      );
      window.open(file.imageUrl, "_blank");
    }
  };

  // Debounce effect:
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
    }, 250);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Reset display limit whenever filters or search change:
  useEffect(() => {
    setDisplayLimit(40);
  }, [filters, debouncedSearch]);

  // --- API FETCH LOGIC ---
  useEffect(() => {
    if (open) {
      fetchImages();
    }
  }, [open]);

  const fetchImages = async () => {
    setLoading(true);

    const toDate = new Date().toISOString();
    const fromDate = new Date(
      new Date().setDate(new Date().getDate() - 30),
    ).toISOString();

    try {
      let formatFromdate = formatDateDate(fromDate).trim();
      let formatTodate = formatDateDate(toDate).trim();

      // Pass optional uploadedBy if needed, or leave blank/undefined
      const result = await fetchIntakeImagesUncategorized(
        authAxios,
        formatFromdate,
        formatTodate,
      );

      if (result.data?.intakeImageUncategorized) {
        setTotalCount(result.data.intakeImageUncategorized.totalCount);

        const mappedData: FileData[] =
          result.data.intakeImageUncategorized.images.map(
            (img: any, index: number) => {
              const { type, typeLabel } = getFileTypeAndLabel(img);
              const fileUrl = img.imageUrl || img.imagePath || "";

              // Clean and bind the filename from the URL automatically
              const fileName = getCleanFileName(fileUrl, img.caption);

              return {
                id: String(img.id ?? index),
                name: fileName,
                caption: img.caption || "No caption",
                type,
                typeLabel,
                uploadedBy:
                  img.uploadedByFirstName ||
                  img.uploadedByUsername ||
                  "Unknown",
                date: formatDate(img.uploadedOn) || "-",
                rawDate: img.uploadedOn,
                size: img.size || "—",
                imageUrl: fileUrl,
              };
            },
          );

        setApiFiles(mappedData);
      } else if (result.errors) {
        console.error("GraphQL Errors:", result.errors);
      }
    } catch (error) {
      console.error("Error fetching files:", error);
    } finally {
      setLoading(false);
    }
  };

  // --- DYNAMIC UPLOADERS LIST ---
  const uniqueUploaders = useMemo(() => {
    const uploaders = new Set<string>();
    apiFiles.forEach((file) => {
      if (file.uploadedBy && file.uploadedBy !== "Unknown") {
        uploaders.add(file.uploadedBy);
      }
    });
    return Array.from(uploaders).sort();
  }, [apiFiles]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.tab !== "all") count++;
    if (filters.fileTypes.length > 0) count++;
    if (filters.date) count++;
    if (filters.uploadedBy.length > 0) count++;
    return count;
  }, [filters]);

  const filteredFiles = useMemo(() => {
    // 1. Filter the files
    const result = apiFiles.filter((file) => {
      if (filters.tab === "photos" && file.type !== "photo") return false;
      if (filters.tab === "files" && file.type === "photo") return false;
      if (
        filters.fileTypes.length > 0 &&
        !filters.fileTypes.includes(file.typeLabel.toUpperCase())
      )
        return false;
      if (
        filters.uploadedBy.length > 0 &&
        !filters.uploadedBy.includes(file.uploadedBy)
      )
        return false;

      // Date filtering logic
      if (filters.date) {
        const fileTime = new Date(file.rawDate || file.date).getTime();
        const now = new Date().getTime();

        if (filters.date === "Today") {
          const todayStart = new Date().setHours(0, 0, 0, 0);
          if (fileTime < todayStart) return false;
        } else if (filters.date === "Last 7 days") {
          const sevenDaysAgo = now - 7 * 24 * 60 * 60 * 1000;
          if (fileTime < sevenDaysAgo) return false;
        } else if (filters.date === "Last 30 days") {
          const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;
          if (fileTime < thirtyDaysAgo) return false;
        } else if (filters.date === "Custom range...") {
          // UNCOMMENTED AND FIXED CUSTOM DATE LOGIC:
          if (customStartDate) {
            // Start of the selected start day
            const startTimestamp = new Date(customStartDate).setHours(
              0,
              0,
              0,
              0,
            );
            if (fileTime < startTimestamp) return false;
          }
          if (customEndDate) {
            // End of the selected end day
            const endTimestamp = new Date(customEndDate).setHours(
              23,
              59,
              59,
              999,
            );
            if (fileTime > endTimestamp) return false;
          }
        }
      }

      // Using debouncedSearch instead of searchQuery to prevent lag!
      if (debouncedSearch.trim() !== "") {
        const lowerQuery = debouncedSearch.toLowerCase();
        if (
          !file.name.toLowerCase().includes(lowerQuery) &&
          !file.caption.toLowerCase().includes(lowerQuery)
        ) {
          return false;
        }
      }
      return true;
    });

    // 2. Sort the files
    result.sort((a, b) => {
      const timeA = new Date(a.rawDate || a.date).getTime();
      const timeB = new Date(b.rawDate || b.date).getTime();

      if (sortOrder === "newest") {
        return timeB - timeA; // Descending (Newest first)
      } else {
        return timeA - timeB; // Ascending (Oldest first)
      }
    });

    return result;
  }, [
    apiFiles,
    filters,
    debouncedSearch,
    sortOrder,
    customStartDate,
    customEndDate,
  ]);
  // ^^^ ADDED debouncedSearch, customStartDate, and customEndDate to the array!

  // --- HANDLERS ---
  const toggleSelection = (id: string, file: FileData) => {
    const isCurrentlySelected = selectedIds.includes(id);
    const newSelected = isCurrentlySelected
      ? selectedIds.filter((item) => item !== id)
      : [...selectedIds, id];

    if (isCurrentlySelected && newSelected.length === 1) {
      const remainingId = newSelected[0];
      const remainingFile = apiFiles.find((f) => f.id === remainingId);
      setActivePreview(remainingFile || file);
    } else {
      setActivePreview(file);
    }

    setSelectedIds(newSelected);
  };

  const getOriginalCaption = (f: FileData) =>
    f.caption === "No caption" ? "" : f.caption;
  const getDraft = (f: FileData) =>
    bulkDrafts[f.id] !== undefined ? bulkDrafts[f.id] : getOriginalCaption(f);
  const isEdited = (f: FileData) =>
    getDraft(f).trim() !== getOriginalCaption(f).trim();

  const selectedFiles = useMemo(
    () =>
      selectedIds
        .map((id) => apiFiles.find((f) => f.id === id))
        .filter(Boolean) as FileData[],
    [selectedIds, apiFiles],
  );
  const changedFiles = selectedFiles.filter(isEdited);

  const applyCaptionUpdates = async (
    updates: { id: string; caption: string }[],
  ) => {
    if (updates.length === 0) return;
    setSavingCaptions(true);
    try {
      const payload = updates.map((u) => ({ id: u.id, caption: u.caption }));

      await CloudFileUpload({
        authAxios,
        payload,
      });

      const map: Record<string, string> = {};
      updates.forEach((u) => {
        map[u.id] = u.caption;
      });
      const patch = (f: FileData): FileData =>
        map[f.id] !== undefined
          ? {
              ...f,
              caption: map[f.id] || "No caption",
              name: map[f.id] || "Untitled Image",
            }
          : f;
      setApiFiles((prev) => prev.map(patch));
      setActivePreview((prev) => (prev ? patch(prev) : prev));
      setBulkDrafts({});
      setEditingSingle(false);
      toast.success("Caption updated successfully!");

      fetchImages();
      setSelectedIds([]);
      setActivePreview(null);
      setSameCaption("");
    } catch (error) {
      console.error("Error saving captions:", error);
      toast.error("Caption not updated");
    } finally {
      setSavingCaptions(false);
    }
  };

  const startSingleEdit = () => {
    if (!activePreview) return;
    setSingleDraft(getOriginalCaption(activePreview));
    setEditingSingle(true);
  };

  const saveSingleEdit = () => {
    if (!activePreview) return;
    applyCaptionUpdates([
      { id: activePreview.id, caption: singleDraft.trim() },
    ]);
  };

  const applySameCaptionToAll = () => {
    const next: Record<string, string> = {};
    selectedFiles.forEach((f) => {
      next[f.id] = sameCaption;
    });
    setBulkDrafts((prev) => ({ ...prev, ...next }));
  };

  // Close single editor when another file is previewed
  useEffect(() => {
    setEditingSingle(false);
  }, [activePreview && activePreview.id]);

  // Handle scroll in the left pane to load more items:
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    if (scrollHeight - scrollTop - clientHeight < 200) {
      setDisplayLimit((prev) => Math.min(prev + 40, filteredFiles.length));
    }
  };

  // Sliced files to display:
  const visibleFiles = useMemo(() => {
    return filteredFiles.slice(0, displayLimit);
  }, [filteredFiles, displayLimit]);

  const groupedVisibleFiles = useMemo(() => {
    if (viewMode !== "grid") return {};

    const groups: Record<string, FileData[]> = {};

    for (const file of visibleFiles) {
      const label = getDateCategoryLabel(file.rawDate);

      if (!groups[label]) {
        groups[label] = [];
      }

      groups[label].push(file);
    }

    return groups;
  }, [visibleFiles, viewMode]);

  const clearAllFilters = () => {
    setFilters({ tab: "all", fileTypes: [], date: "", uploadedBy: [] });
    setSearchQuery("");
    setCustomStartDate(""); // <-- ADD
    setCustomEndDate(""); // <-- ADD
  };

  const handleFilterToggle = (
    category: keyof typeof filters,
    value: string,
  ) => {
    setFilters((prev) => {
      const current = prev[category];
      if (Array.isArray(current)) {
        return {
          ...prev,
          [category]: current.includes(value)
            ? current.filter((v) => v !== value)
            : [...current, value],
        };
      } else {
        return { ...prev, [category]: current === value ? "" : value };
      }
    });
  };

  const removeFilter = (
    category: keyof typeof filters,
    arrayValueToRemove?: string,
  ) => {
    if (arrayValueToRemove) {
      handleFilterToggle(category, arrayValueToRemove);
    } else {
      setFilters((prev) => ({
        ...prev,
        [category]: category === "tab" ? "all" : "",
      }));
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth={false}
      classes={{ paper: classes.dialogPaper }}
    >
      {/* HEADER */}
      <Box className={classes.header}>
        <Box display="flex" alignItems="center" gridGap={16}>
          <Box
            p={1}
            bgcolor="#e8f0fe"
            color="#1a73e8"
            borderRadius={8}
            display="flex"
          >
            <CloudQueue />
          </Box>
          <Box>
            <Typography variant="caption" color="textSecondary">
              LSDB CLOUD
            </Typography>
            <Typography variant="h6" style={{ fontWeight: 600, lineHeight: 1 }}>
              {filteredFiles.length} of {totalCount || apiFiles.length} items ·
              photos & files
            </Typography>
          </Box>
        </Box>
        <Box display="flex" gridGap={12}>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            className={classes.actionButton}
            style={{ backgroundColor: "#2d68e1" }}
            onClick={() => {
              handleClose(); // Close the dialog first
              history.push("/operations/upload_cloud"); // Redirect to the upload page
            }}
          >
            Upload
          </Button>
          <IconButton
            onClick={handleClose}
            size="small"
            // style={{ backgroundColor: "#f1f3f4" }}
          >
            <Close />
          </IconButton>
        </Box>
      </Box>

      {/* TOOLBAR */}
      <Box className={classes.toolbar}>
        <Box display="flex" bgcolor="#e8eaed" borderRadius={4} p={0.5}>
          <Button
            size="small"
            onClick={() => setFilters((p) => ({ ...p, tab: "all" }))}
            style={{
              backgroundColor: filters.tab === "all" ? "#fff" : "transparent",
              fontWeight: filters.tab === "all" ? "bold" : "normal",
              color: filters.tab === "all" ? "#1a73e8" : "inherit", // Turns blue when selected
              padding: "4px 24px", // Adds left and right padding
              textTransform: "none",
            }}
          >
            All ({apiFiles.length})
          </Button>
          <Button
            size="small"
            onClick={() => setFilters((p) => ({ ...p, tab: "photos" }))}
            style={{
              backgroundColor:
                filters.tab === "photos" ? "#fff" : "transparent",
              fontWeight: filters.tab === "photos" ? "bold" : "normal",
              color: filters.tab === "photos" ? "#1a73e8" : "inherit", // Turns blue when selected
              padding: "4px 24px", // Adds left and right padding
              textTransform: "none",
            }}
          >
            Photos ({apiFiles.filter((f) => f.type === "photo").length})
          </Button>
          <Button
            size="small"
            onClick={() => setFilters((p) => ({ ...p, tab: "files" }))}
            style={{
              backgroundColor: filters.tab === "files" ? "#fff" : "transparent",
              fontWeight: filters.tab === "files" ? "bold" : "normal",
              color: filters.tab === "files" ? "#1a73e8" : "inherit", // Turns blue when selected
              padding: "4px 24px", // Adds left and right padding
              textTransform: "none",
            }}
          >
            Files ({apiFiles.filter((f) => f.type !== "photo").length})
          </Button>
        </Box>
        <Box className={classes.searchBox}>
          <Search fontSize="small" color="action" style={{ marginRight: 8 }} />
          <InputBase
            placeholder="Search caption or filename..."
            fullWidth
            style={{ fontSize: 14 }}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </Box>

        <Button
          startIcon={<FilterList />}
          variant={activeFilterCount > 0 ? "contained" : "outlined"}
          color={activeFilterCount > 0 ? "primary" : "default"}
          size="small"
          style={
            activeFilterCount > 0
              ? { backgroundColor: "#2d68e1", color: "#fff" }
              : {}
          }
          onClick={(e) => setFilterAnchorEl(e.currentTarget)}
        >
          {activeFilterCount > 0 ? `Filter · ${activeFilterCount}` : "Filter"}
        </Button>
        <Button
          startIcon={
            <Sort
              style={{
                transform: sortOrder === "oldest" ? "scaleY(-1)" : "none",
                transition: "transform 0.2s",
              }}
            />
          }
          variant="outlined"
          size="small"
          onClick={() =>
            setSortOrder((prev) => (prev === "newest" ? "oldest" : "newest"))
          }
        >
          Sort: {sortOrder === "newest" ? "Newest" : "Oldest"}
        </Button>

        <Box flexGrow={1} />

        <Box display="flex" bgcolor="#e8eaed" borderRadius={4} p={0.5}>
          <Button
            size="small"
            startIcon={<ViewModule />} // <-- Adds Grid Icon
            onClick={() => setViewMode("grid")}
            style={{
              backgroundColor: viewMode === "grid" ? "#fff" : "transparent",
              fontWeight: viewMode === "grid" ? "bold" : "normal",
              color: viewMode === "grid" ? "#1a73e8" : "inherit", // Turns blue when active
              padding: "4px 20px", // Adds left and right padding
              textTransform: "none",
            }}
          >
            Grid
          </Button>
          <Button
            size="small"
            startIcon={<ViewList />} // <-- Adds List Icon
            onClick={() => setViewMode("list")}
            style={{
              backgroundColor: viewMode === "list" ? "#fff" : "transparent",
              fontWeight: viewMode === "list" ? "bold" : "normal",
              color: viewMode === "list" ? "#1a73e8" : "inherit", // Turns blue when active
              padding: "4px 20px", // Adds left and right padding
              textTransform: "none",
            }}
          >
            List
          </Button>
        </Box>
      </Box>

      {/* ACTIVE CHIPS ROW */}
      {activeFilterCount > 0 && (
        <Box className={classes.chipRow}>
          {filters.tab !== "all" && (
            <Chip
              label={filters.tab === "photos" ? "Photos" : "Files"}
              onDelete={() => removeFilter("tab")}
              className={classes.chipActive}
              size="small"
            />
          )}
          {filters.fileTypes.length > 0 && (
            <Chip
              label={filters.fileTypes.join(" · ")}
              onDelete={() => setFilters((p) => ({ ...p, fileTypes: [] }))}
              className={classes.chipActive}
              size="small"
            />
          )}
          {filters.uploadedBy.map((user) => (
            <Chip
              key={user}
              label={user}
              onDelete={() => removeFilter("uploadedBy", user)}
              className={classes.chipActive}
              size="small"
            />
          ))}
          {filters.date && (
            <Chip
              label={filters.date}
              onDelete={() => removeFilter("date")}
              className={classes.chipActive}
              size="small"
            />
          )}
        </Box>
      )}

      {/* MAIN CONTENT SPLIT */}
      <Box className={classes.mainContent}>
        {/* LEFT PANE - LIST OR GRID */}
        {/* LEFT PANE */}
        <Box className={classes.leftPane} onScroll={handleScroll}>
          {loading ? (
            <Box className={classes.loadingContainer}>
              <CircularProgress />
              <Typography color="textSecondary">Loading files...</Typography>
            </Box>
          ) : filteredFiles.length === 0 ? (
            <Box p={5} textAlign="center">
              <Typography color="textSecondary">
                No files found matching the selected filters.
              </Typography>
            </Box>
          ) : viewMode === "list" ? (
            <Table stickyHeader size="small">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox"></TableCell>
                  <TableCell
                    style={{ fontSize: 12, fontWeight: 600, color: "#888" }}
                  >
                    NAME / CAPTION
                  </TableCell>
                  <TableCell
                    style={{ fontSize: 12, fontWeight: 600, color: "#888" }}
                  >
                    TYPE
                  </TableCell>
                  <TableCell
                    style={{ fontSize: 12, fontWeight: 600, color: "#888" }}
                  >
                    UPLOADED BY
                  </TableCell>
                  <TableCell
                    style={{ fontSize: 12, fontWeight: 600, color: "#1a73e8" }}
                  >
                    DATE ↑
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleFiles.map((file) => {
                  const selectionIndex = selectedIds.indexOf(file.id);
                  const isSelected = selectionIndex !== -1;
                  return (
                    <TableRow
                      key={file.id}
                      hover
                      selected={isSelected}
                      style={{
                        backgroundColor: isSelected ? "#f8faff" : "inherit",
                        cursor: "pointer",
                      }}
                      onClick={() => setActivePreview(file)}
                    >
                      <TableCell padding="checkbox">
                        <Box
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleSelection(file.id, file);
                          }}
                          style={{
                            width: 22,
                            height: 22,
                            borderRadius: "50%",
                            border: isSelected ? "none" : "2px solid #ccc",
                            backgroundColor: isSelected
                              ? "#1a73e8"
                              : "transparent",
                            color: "#fff",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            fontSize: 12,
                            fontWeight: "bold",
                            cursor: "pointer",
                            marginLeft: 12,
                          }}
                        >
                          {isSelected ? selectionIndex + 1 : ""}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" alignItems="center" gridGap={12}>
                          {file.type === "photo" && file.imageUrl ? (
                            <Box
                              width={40}
                              height={40}
                              flexShrink={0}
                              borderRadius={4}
                              overflow="hidden"
                              bgcolor="#f1f3f4"
                            >
                              <img
                                src={file.imageUrl}
                                alt={file.name}
                                loading="lazy"
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  objectFit: "cover",
                                }}
                              />
                            </Box>
                          ) : (
                            getFileIcon(file.type, classes)
                          )}
                          <Box minWidth={0}>
                            <Typography
                              variant="body2"
                              style={{
                                fontWeight: 600,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                              }}
                            >
                              {file.name}
                            </Typography>
                            <Typography
                              variant="caption"
                              color="textSecondary"
                              style={{
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                whiteSpace: "nowrap",
                                display: "block",
                              }}
                            >
                              {file.caption}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {file.typeLabel}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {file.uploadedBy}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="textSecondary">
                          {file.date}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          ) : (
            <>
              <Box width="100%">
                {Object.entries(groupedVisibleFiles).map(
                  ([groupLabel, filesInGroup]) => {
                    const photoCount = filesInGroup.filter(
                      (f) => f.type === "photo",
                    ).length;
                    const fileCount = filesInGroup.length - photoCount;
                    const groupSelectedIds = filesInGroup.map((f) => f.id);
                    const isAllGroupSelected = groupSelectedIds.every((id) =>
                      selectedIds.includes(id),
                    );

                    const handleSelectAllGroup = () => {
                      if (isAllGroupSelected) {
                        setSelectedIds((prev) =>
                          prev.filter((id) => !groupSelectedIds.includes(id)),
                        );
                      } else {
                        setSelectedIds((prev) =>
                          Array.from(new Set([...prev, ...groupSelectedIds])),
                        );
                      }
                    };

                    return (
                      <Box key={groupLabel} mb={3}>
                        {/* Group Header Banner */}
                        <Box
                          display="flex"
                          justifyContent="space-between"
                          alignItems="center"
                          px={3}
                          py={1}
                          bgcolor="#f8f9fa"
                          borderTop="1px solid #e0e0e0"
                          borderBottom="1px solid #e0e0e0"
                        >
                          <Typography
                            variant="body2"
                            style={{ fontWeight: 600, color: "#333" }}
                          >
                            {groupLabel} · {photoCount} photos · {fileCount}{" "}
                            files
                          </Typography>
                          <Button
                            size="small"
                            onClick={handleSelectAllGroup}
                            style={{
                              textTransform: "none",
                              color: "#0054C5",
                              fontWeight: 600,
                            }}
                          >
                            {isAllGroupSelected ? "Deselect All" : "Select All"}
                          </Button>
                        </Box>

                        {/* Grid Container for this group */}
                        <Box className={classes.gridContainer}>
                          {filesInGroup.map((file) => {
                            const selectionIndex = selectedIds.indexOf(file.id);
                            const isSelected = selectionIndex !== -1;
                            return (
                              <Box
                                key={file.id}
                                className={`${classes.gridCard} ${isSelected ? classes.gridCardSelected : ""}`}
                                onClick={() => setActivePreview(file)}
                              >
                                <Box
                                  position="absolute"
                                  top={8}
                                  left={8}
                                  zIndex={2}
                                >
                                  <Box
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      toggleSelection(file.id, file);
                                    }}
                                    style={{
                                      width: 22,
                                      height: 22,
                                      borderRadius: "50%",
                                      border: isSelected
                                        ? "none"
                                        : "2px solid #ccc",
                                      backgroundColor: isSelected
                                        ? "#0054C5"
                                        : "rgba(255,255,255,0.9)",
                                      color: "#fff",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: 12,
                                      fontWeight: "bold",
                                      cursor: "pointer",
                                      boxShadow: isSelected
                                        ? "0 2px 4px rgba(0,0,0,0.2)"
                                        : "none",
                                    }}
                                  >
                                    {isSelected ? selectionIndex + 1 : ""}
                                  </Box>
                                </Box>
                                <Box className={classes.gridCardPreview}>
                                  {file.type === "photo" && file.imageUrl ? (
                                    <img
                                      src={file.imageUrl}
                                      alt={file.caption}
                                      loading="lazy"
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  ) : (
                                    getFileIcon(file.type, classes)
                                  )}
                                </Box>
                                <Typography
                                  variant="body2"
                                  style={{
                                    fontWeight: 600,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {file.name}
                                </Typography>
                                <Typography
                                  variant="caption"
                                  color="textSecondary"
                                  style={{
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    fontFamily: "monospace",
                                  }}
                                >
                                  {file.caption}
                                </Typography>
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  mt={1}
                                >
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    noWrap
                                    style={{
                                      maxWidth: "60%",
                                      fontFamily: "monospace",
                                    }}
                                  >
                                    {file.uploadedBy}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="textSecondary"
                                    style={{ fontFamily: "monospace" }}
                                  >
                                    {file.date}
                                  </Typography>
                                </Box>
                              </Box>
                            );
                          })}
                        </Box>
                      </Box>
                    );
                  },
                )}
              </Box>
            </>
          )}
        </Box>

        {/* RIGHT PANE - CONDITIONAL PREVIEW */}

        {activePreview && selectedIds.length <= 1 && (
          <Box className={classes.rightPane}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={1}
            >
              <Typography
                variant="overline"
                color="textSecondary"
                style={{ marginBottom: 0, fontSize: 10 }}
              >
                PREVIEW
              </Typography>
              <IconButton size="small" onClick={() => setActivePreview(null)}>
                <Close fontSize="small" />
              </IconButton>
            </Box>

            <Box className={classes.previewBox}>
              {activePreview.type === "photo" && activePreview.imageUrl ? (
                <img
                  src={activePreview.imageUrl}
                  alt={activePreview.caption}
                  style={{
                    maxWidth: "100%",
                    maxHeight: "100%",
                    objectFit: "contain",
                  }}
                  onError={(e) => {
                    console.error(
                      "Failed to load image URL:",
                      activePreview.imageUrl,
                    );
                  }}
                />
              ) : (
                <Box
                  bgcolor={
                    {
                      photo: "#2c3e50",
                      pdf: "#e53935",
                      doc: "#1e88e5",
                      xls: "#43a047",
                      csv: "#00acc1",
                      zip: "#fb8c00",
                    }[activePreview.type] || "#757575"
                  }
                  style={{
                    width: 90,
                    height: 90,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: 24,
                    color: "#fff",
                    borderRadius: 8,
                  }}
                >
                  {activePreview.type === "photo"
                    ? "IMG"
                    : activePreview.typeLabel.toUpperCase()}
                </Box>
              )}
            </Box>

            <Typography
              variant="body2"
              style={{ fontWeight: 400, fontSize: 15, marginBottom: 8 }}
            >
              {activePreview.name}
            </Typography>

            {editingSingle ? (
              <Box mb={2}>
                <TextField
                  autoFocus
                  fullWidth
                  multiline
                  variant="outlined"
                  size="small"
                  placeholder="Add a caption"
                  value={singleDraft}
                  onChange={(e) => setSingleDraft(e.target.value)}
                  inputProps={{ style: { fontSize: 12 } }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      saveSingleEdit();
                    }
                    if (e.key === "Escape") setEditingSingle(false);
                  }}
                />
                <Box
                  display="flex"
                  justifyContent="flex-end"
                  gridGap={8}
                  mt={1}
                >
                  <Button
                    size="small"
                    style={{ textTransform: "none", fontSize: 11 }}
                    onClick={() => setEditingSingle(false)}
                    disabled={savingCaptions}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    disableElevation
                    style={{
                      textTransform: "none",
                      backgroundColor: "#0054C5",
                      color: "#fff",
                      fontSize: 11,
                    }}
                    onClick={saveSingleEdit}
                    disabled={
                      savingCaptions ||
                      singleDraft.trim() ===
                        getOriginalCaption(activePreview).trim()
                    }
                  >
                    {savingCaptions ? "Saving..." : "Save"}
                  </Button>
                </Box>
              </Box>
            ) : (
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                mb={2}
              >
                {/* LEFT: Caption and Edit icon side-by-side */}
                <Box display="flex" alignItems="center" gridGap={4}>
                  <Typography
                    variant="caption"
                    color="textSecondary"
                    style={{
                      fontFamily: "monospace",
                      fontSize: 14,
                      fontWeight: 600,
                    }}
                  >
                    {activePreview.caption}
                  </Typography>
                  <IconButton
                    size="small"
                    title="Edit caption"
                    onClick={startSingleEdit}
                    style={{ color: "#0054C5", padding: 4 }}
                  >
                    <Edit style={{ fontSize: 16 }} />
                  </IconButton>
                </Box>

                {/* RIGHT: Download and Delete moved up here */}
                <Box display="flex" gridGap={8}>
                  <IconButton
                    onClick={() => {
                      if (selectedIds.length > 1) {
                        handleBatchDownload();
                      } else if (activePreview) {
                        handleDownload(activePreview);
                      }
                    }}
                    disabled={
                      !activePreview?.imageUrl && selectedIds.length === 0
                    }
                    style={{
                      color: "#0054C5",
                      border: "1px solid #0054C5",
                      borderRadius: 4,
                      padding: 6,
                    }}
                  >
                    <GetApp style={{ fontSize: 20 }} />
                  </IconButton>

                  <IconButton
                    style={{
                      color: "#d32f2f",
                      border: "1px solid #ffcdd2",
                      borderRadius: 4,
                      padding: 6,
                    }}
                  >
                    <DeleteOutline style={{ fontSize: 20 }} />
                  </IconButton>
                </Box>
              </Box>
            )}

            <Box display="flex" flexWrap="wrap" gridGap={16} mb={3}>
              <Box width="45%">
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ fontSize: 10 }}
                >
                  UPLOADED BY
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  style={{ fontWeight: 600, fontSize: 12 }}
                >
                  {activePreview.uploadedBy}
                </Typography>
              </Box>
              <Box width="45%">
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ fontSize: 10 }}
                >
                  DATE & TIME
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  style={{ fontWeight: 600, fontSize: 12 }}
                >
                  {activePreview.date}
                </Typography>
              </Box>
              <Box width="45%">
                <Typography
                  variant="caption"
                  color="textSecondary"
                  style={{ fontSize: 10 }}
                >
                  TYPE
                </Typography>
                <Typography
                  variant="caption"
                  display="block"
                  style={{ fontWeight: 600, fontSize: 12 }}
                >{`${activePreview.typeLabel} · ${activePreview.caption.split(".").pop()?.toUpperCase()}`}</Typography>
              </Box>
            </Box>
          </Box>
        )}
        {selectedIds.length > 1 && (
          <Box
            className={classes.rightPane}
            style={{ padding: 0, overflow: "hidden" }}
          >
            {/* header */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              px={3}
              pt={2.5}
              pb={1}
            >
              <Box display="flex" alignItems="center" gridGap={8}>
                <ImageIcon fontSize="small" style={{ color: "#667085" }} />
                <Typography
                  variant="overline"
                  style={{ fontWeight: 700, lineHeight: 1.5 }}
                >
                  PHOTOS{" "}
                  <span style={{ color: "#98a2b3", fontWeight: 400 }}>
                    · {selectedFiles.length}
                  </span>
                </Typography>
              </Box>
              <Typography variant="caption" color="textSecondary">
                Captions optional
              </Typography>
            </Box>

            {/* scrollable body */}
            <Box flex={1} overflow="auto" px={3} pb={2}>
              {/* same caption for all */}
              <Box
                p={2}
                mb={2}
                borderRadius={12}
                style={{
                  backgroundColor: "#fff",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Box display="flex" alignItems="center" gridGap={10} mb={1.5}>
                  <Box
                    display="flex"
                    p={0.75}
                    borderRadius={8}
                    bgcolor="#f8f9fa"
                    style={{
                      boxShadow: "0 1px 2px rgba(0,0,0,0.08)",
                      color: "#0054C5",
                    }}
                  >
                    <FileCopyOutlined fontSize="small" />
                  </Box>
                  <Typography variant="body2" style={{ fontWeight: 600 }}>
                    Same caption for all {selectedFiles.length} photos
                  </Typography>
                </Box>
                <Box display="flex" gridGap={8}>
                  <TextField
                    fullWidth
                    variant="outlined"
                    size="small"
                    placeholder="e.g. Crate 12 — front label"
                    value={sameCaption}
                    onChange={(e) => setSameCaption(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && sameCaption.trim())
                        applySameCaptionToAll();
                    }}
                    InputProps={{ style: { backgroundColor: "#fff" } }}
                  />
                  <Button
                    variant="contained"
                    disableElevation
                    disabled={!sameCaption.trim()}
                    onClick={applySameCaptionToAll}
                    style={{
                      textTransform: "none",
                      whiteSpace: "nowrap",
                      backgroundColor: sameCaption.trim()
                        ? "#0054C5"
                        : undefined,
                      color: sameCaption.trim() ? "#fff" : undefined,
                    }}
                  >
                    Apply to all {selectedFiles.length}
                  </Button>
                </Box>
              </Box>

              {/* per-photo rows */}
              <Box
                borderRadius={12}
                overflow="hidden"
                style={{ border: "1px solid #e0e0e0" }}
              >
                {selectedFiles.map((f, i) => {
                  const edited = isEdited(f);
                  return (
                    <Box
                      key={f.id}
                      display="flex"
                      alignItems="center"
                      gridGap={12}
                      p={1.5}
                      style={{
                        borderTop: i === 0 ? "none" : "1px solid #fffff",
                        backgroundColor: edited ? "#ffffff" : "#fff",
                      }}
                    >
                      <Box
                        width={56}
                        height={56}
                        flexShrink={0}
                        borderRadius={8}
                        overflow="hidden"
                        bgcolor="#f1f3f4"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        {f.imageUrl ? (
                          <img
                            src={f.imageUrl}
                            alt={f.caption}
                            loading="lazy"
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                        ) : (
                          <ImageIcon style={{ color: "#aaa" }} />
                        )}
                      </Box>
                      <Box flex={1} minWidth={0}>
                        <Box
                          display="flex"
                          alignItems="center"
                          gridGap={8}
                          mb={0.5}
                        >
                          <Typography
                            variant="caption"
                            color="textSecondary"
                            noWrap
                            style={{ fontFamily: "monospace" }}
                          >
                            {f.name}
                          </Typography>
                          {edited && (
                            <Chip
                              label="Edited"
                              size="small"
                              style={{
                                height: 18,
                                fontSize: 11,
                                backgroundColor: "#ffffff",
                                color: "#0054C5",
                                fontWeight: 600,
                              }}
                            />
                          )}
                        </Box>
                        <TextField
                          fullWidth
                          variant="outlined"
                          size="small"
                          placeholder="Add a caption"
                          value={getDraft(f)}
                          onChange={(e) =>
                            setBulkDrafts((prev) => ({
                              ...prev,
                              [f.id]: e.target.value,
                            }))
                          }
                        />
                      </Box>
                      <IconButton
                        size="small"
                        title="Reset caption"
                        disabled={!edited}
                        onClick={() =>
                          setBulkDrafts((prev) => {
                            const n = { ...prev };
                            delete n[f.id];
                            return n;
                          })
                        }
                        style={{ backgroundColor: "#ffffff" }}
                      >
                        <Replay fontSize="small" />
                      </IconButton>
                    </Box>
                  );
                })}
              </Box>

              <Box
                display="flex"
                alignItems="center"
                gridGap={6}
                mt={1.5}
                color="#667085"
              >
                <InfoOutlined style={{ fontSize: 16 }} />
                <Typography variant="caption" color="textSecondary">
                  Apply one caption to everything, then tweak any row by hand.
                </Typography>
              </Box>
            </Box>

            {/* download + delete */}
            <Box
              display="flex"
              justifyContent="flex-end"
              gridGap={12}
              px={3}
              py={1.5}
              style={{ borderTop: "1px solid #e0e0e0" }}
            >
              <IconButton
                onClick={handleBatchDownload}
                style={{
                  color: "#0054C5",
                  border: "1px solid #0054C5",
                  borderRadius: 4,
                  padding: 8,
                }}
              >
                <GetApp />
              </IconButton>
              <IconButton
                style={{
                  color: "#d32f2f",
                  border: "1px solid #ffcdd2",
                  borderRadius: 4,
                  padding: 8,
                }}
              >
                <DeleteOutline />
              </IconButton>
            </Box>

            {/* save bar */}
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              px={3}
              py={1.5}
              bgcolor="#f9fafb"
              style={{ borderTop: "1px solid #e0e0e0" }}
            >
              <Box display="flex" alignItems="center" gridGap={8}>
                <Box
                  width={8}
                  height={8}
                  borderRadius="50%"
                  bgcolor={changedFiles.length ? "#0054C5" : "#cfd4dc"}
                />
                <Typography variant="body2" style={{ fontWeight: 600 }}>
                  {changedFiles.length} caption
                  {changedFiles.length === 1 ? "" : "s"} changed
                </Typography>
              </Box>
              <Box display="flex" gridGap={8}>
                <Button
                  size="small"
                  style={{ textTransform: "none" }}
                  disabled={changedFiles.length === 0 || savingCaptions}
                  onClick={() => {
                    setBulkDrafts({});
                    setSameCaption("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  disableElevation
                  disabled={changedFiles.length === 0 || savingCaptions}
                  onClick={() =>
                    applyCaptionUpdates(
                      changedFiles.map((f) => ({
                        id: f.id,
                        caption: getDraft(f).trim(),
                      })),
                    )
                  }
                  style={{
                    textTransform: "none",
                    backgroundColor: changedFiles.length
                      ? "#0054C5"
                      : undefined,
                    color: changedFiles.length ? "#fff" : undefined,
                  }}
                >
                  {savingCaptions
                    ? "Saving..."
                    : `Save ${changedFiles.length} caption${changedFiles.length === 1 ? "" : "s"}`}
                </Button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>

      {/* FOOTER */}
      <Box className={classes.footer}>
        <Box display="flex" alignItems="center" gridGap={12}>
          <Typography variant="body2" style={{ fontWeight: 600 }}>
            {selectedIds.length} selected
          </Typography>
          <Button
            size="small"
            color="primary"
            style={{ textTransform: "none" }}
            onClick={() => setSelectedIds([])}
          >
            Clear
          </Button>
        </Box>
        <Box display="flex" gridGap={12}>
          <Button
            variant="outlined"
            onClick={handleClose}
            className={classes.actionButton}
          >
            Cancel
          </Button>
          {/* <Button variant="contained" color="primary" className={classes.actionButton} style={{ backgroundColor: "#2d68e1" }} disabled={selectedIds.length === 0}>
            Add {selectedIds.length > 0 ? selectedIds.length : ''} to module
          </Button> */}
        </Box>
      </Box>

      {/* FILTER POPOVER */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={() => setFilterAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        classes={{ paper: classes.popoverPaper }}
      >
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          mb={1}
        >
          <Typography variant="h6" style={{ fontWeight: 600 }}>
            Filter
          </Typography>
          <Button
            size="small"
            color="primary"
            onClick={clearAllFilters}
            style={{ textTransform: "none", fontWeight: 600 }}
          >
            Clear all
          </Button>
        </Box>

        <Typography className={classes.filterSectionLabel}>TYPE</Typography>
        <Box display="flex" flexWrap="wrap">
          {["all", "photos", "files"].map((t) => (
            <Button
              key={t}
              className={`${classes.filterBtn} ${filters.tab === t ? classes.filterBtnActive : ""}`}
              onClick={() => handleFilterToggle("tab", t)}
            >
              {t.charAt(0).toUpperCase() + t.slice(1)}
            </Button>
          ))}
        </Box>

        <Typography className={classes.filterSectionLabel}>
          FILE TYPE
        </Typography>
        <Box display="flex" flexWrap="wrap">
          {["PDF", "DOC", "XLS", "ZIP", "CSV"].map((ft) => (
            <Button
              key={ft}
              className={`${classes.filterBtn} ${filters.fileTypes.includes(ft) ? classes.filterBtnActive : ""}`}
              onClick={() => handleFilterToggle("fileTypes", ft)}
            >
              {ft}
            </Button>
          ))}
        </Box>

        <Typography className={classes.filterSectionLabel}>DATE</Typography>
        <Box display="flex" flexWrap="wrap">
          {["Today", "Last 7 days", "Last 30 days", "Custom range..."].map(
            (dt) => (
              <Button
                key={dt}
                className={`${classes.filterBtn} ${filters.date === dt ? classes.filterBtnActive : ""}`}
                onClick={() => handleFilterToggle("date", dt)}
              >
                {dt}
              </Button>
            ),
          )}
        </Box>

        {filters.date === "Custom range..." && (
          <Box display="flex" gridGap={12} mt={1} mb={2}>
            <TextField
              type="date"
              size="small"
              variant="outlined"
              fullWidth
              value={customStartDate}
              onChange={(e) => setCustomStartDate(e.target.value)}
            />
            <Typography variant="body2" style={{ alignSelf: "center" }}>
              to
            </Typography>
            <TextField
              type="date"
              size="small"
              variant="outlined"
              fullWidth
              value={customEndDate}
              onChange={(e) => setCustomEndDate(e.target.value)}
            />
          </Box>
        )}

        <Typography className={classes.filterSectionLabel}>
          UPLOADED BY
        </Typography>
        <Box
          display="flex"
          flexWrap="wrap"
          style={{ maxHeight: 120, overflowY: "auto" }}
        >
          {uniqueUploaders.length > 0 ? (
            uniqueUploaders.map((ub) => (
              <Button
                key={ub}
                className={`${classes.filterBtn} ${filters.uploadedBy.includes(ub) ? classes.filterBtnActive : ""}`}
                onClick={() => handleFilterToggle("uploadedBy", ub)}
              >
                {ub}
              </Button>
            ))
          ) : (
            <Typography variant="caption" color="textSecondary">
              No uploaders found
            </Typography>
          )}
        </Box>

        <Typography className={classes.filterSectionLabel}>CAPTION</Typography>
        <OutlinedInput
          placeholder="e.g. crate 12"
          fullWidth
          margin="dense"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          style={{ height: 40, marginBottom: 24, fontSize: 14 }}
        />

        <Box display="flex" gridGap={12}>
          <Button
            variant="outlined"
            fullWidth
            onClick={() => setFilterAnchorEl(null)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            fullWidth
            style={{ backgroundColor: "#2d68e1" }}
            onClick={() => setFilterAnchorEl(null)}
          >
            Show {filteredFiles.length} results
          </Button>
        </Box>
      </Popover>
    </Dialog>
  );
};
